import { forwardRef, useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import clsx from 'clsx'
import styles from './Field.module.scss'

interface FieldChromeProps {
  label?: ReactNode
  hint?: ReactNode
  error?: ReactNode
  required?: boolean
  className?: string
}

/** Label + control + hint/error wrapper. Use with any control, or via Input/Select/Textarea. */
export function FormField({
  id,
  label,
  hint,
  error,
  required,
  className,
  children,
}: FieldChromeProps & { id?: string; children: ReactNode }) {
  return (
    <div className={clsx(styles.field, className)}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
          {required && (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      {children}
      {error ? (
        <div className={styles.error} role="alert">
          {error}
        </div>
      ) : hint ? (
        <div className={styles.hint}>{hint}</div>
      ) : null}
    </div>
  )
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, FieldChromeProps {
  leftIcon?: ReactNode
  /** Decorative icon on the right; not clickable. */
  rightIcon?: ReactNode
  /** Interactive control on the right, such as a show/hide password button. */
  rightAction?: ReactNode
  size?: 'sm' | 'md'
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, className, leftIcon, rightIcon, rightAction, size = 'md', id: idProp, ...rest },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  const hasRight = !!rightIcon || !!rightAction
  return (
    <FormField id={id} label={label} hint={hint} error={error} required={required} className={className}>
      <div className={styles.control}>
        {leftIcon && <span className={clsx(styles.adornment, styles.left)}>{leftIcon}</span>}
        <input
          ref={ref}
          id={id}
          className={clsx(
            styles.input,
            size === 'sm' && styles.sm,
            leftIcon && styles.withLeft,
            hasRight && styles.withRight,
            error && styles.invalid,
          )}
          aria-invalid={error ? true : undefined}
          required={required}
          {...rest}
        />
        {rightAction ? (
          <span className={clsx(styles.adornment, styles.right, styles.interactive)}>{rightAction}</span>
        ) : rightIcon ? (
          <span className={clsx(styles.adornment, styles.right)}>{rightIcon}</span>
        ) : null}
      </div>
    </FormField>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, FieldChromeProps {}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, className, id: idProp, ...rest },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  return (
    <FormField id={id} label={label} hint={hint} error={error} required={required} className={className}>
      <textarea
        ref={ref}
        id={id}
        className={clsx(styles.textarea, error && styles.invalid)}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </FormField>
  )
})

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'>, FieldChromeProps {
  options: SelectOption[]
  placeholder?: string
  size?: 'sm' | 'md'
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, required, className, options, placeholder, size = 'md', id: idProp, value, ...rest },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  return (
    <FormField id={id} label={label} hint={hint} error={error} required={required} className={className}>
      <select
        ref={ref}
        id={id}
        className={clsx(styles.select, size === 'sm' && styles.sm, error && styles.invalid)}
        value={value ?? ''}
        {...rest}
      >
        {placeholder !== undefined && <option value="">{placeholder}</option>}
        {options.map((opt) => (
          <option key={opt.value} value={opt.value} disabled={opt.disabled}>
            {opt.label}
          </option>
        ))}
      </select>
    </FormField>
  )
})

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode
}

export function Checkbox({ label, className, ...rest }: CheckboxProps) {
  return (
    <label className={clsx(styles.checkRow, className)}>
      <input type="checkbox" className={styles.checkbox} {...rest} />
      {label && <span>{label}</span>}
    </label>
  )
}

export interface SwitchProps {
  checked: boolean
  onChange: (next: boolean) => void
  label?: ReactNode
  disabled?: boolean
  className?: string
}

export function Switch({ checked, onChange, label, disabled, className }: SwitchProps) {
  return (
    <label className={clsx(styles.checkRow, className)}>
      {label && <span>{label}</span>}
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className={clsx(styles.switch, checked && styles.switchOn)}
        onClick={() => onChange(!checked)}
      />
    </label>
  )
}
